<template>
  <div class="participants-panel">
    <div v-if="campaignStatus === 'draft'" class="participants-empty">
      <i class="ri-group-line"></i>
      <p>活動尚未啟用，啟用後可在此查看參與者進度。</p>
    </div>

    <template v-else>
      <div class="participants-toolbar">
        <div class="filter-group">
          <label>狀態</label>
          <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
            <option value="">全部</option>
            <option value="active">進行中</option>
            <option value="completed">已完成</option>
            <option value="expired">已過期</option>
            <option value="cancelled">已取消</option>
          </select>
        </div>
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="loading" @click="load">
          <i class="ri-refresh-line"></i>
          重新整理
        </button>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入參與者...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button type="button" class="btn btn-secondary btn-sm" @click="load">重試</button>
      </div>

      <template v-else>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>會員 ID</th>
                <th>LINE UID</th>
                <th>目前步驟</th>
                <th>狀態</th>
                <th>加入時間</th>
                <th>完成時間</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="participants.length === 0">
                <td colspan="6" class="empty-state">尚無參與者</td>
              </tr>
              <tr v-for="item in participants" :key="item.id">
                <td>{{ item.memberId || '-' }}</td>
                <td class="mono">{{ truncate(item.lineUserId) }}</td>
                <td>
                  <span v-if="item.currentStepOrder">{{ item.currentStepOrder }}. {{ item.currentStepName }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <span :class="statusBadgeClass(item.status)">{{ statusLabel(item.status) }}</span>
                </td>
                <td>{{ formatDate(item.joinedAt) }}</td>
                <td>{{ formatDate(item.completedAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <ListPagination
          v-if="pagination.totalPages > 1"
          :page="currentPage"
          :total-pages="pagination.totalPages"
          :total="pagination.totalItems"
          @change="goToPage"
        />
      </template>
    </template>
  </div>
</template>

<script>
import { ref, reactive, watch, onMounted } from 'vue';
import { marketingCampaignService } from '@/api/modules/marketingCampaigns.js';

export default {
  name: 'MarketingCampaignParticipantsPanel',
  props: {
    campaignId: { type: String, required: true },
    campaignStatus: { type: String, default: 'draft' }
  },
  setup(props) {
    const loading = ref(false);
    const error = ref(null);
    const participants = ref([]);
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = 20;
    const pagination = reactive({
      totalItems: 0,
      totalPages: 1
    });

    const load = async () => {
      if (!props.campaignId || props.campaignStatus === 'draft') return;
      loading.value = true;
      error.value = null;
      try {
        const params = {
          page: currentPage.value,
          pageSize
        };
        if (filterStatus.value) params.status = filterStatus.value;

        const res = await marketingCampaignService.getParticipants(props.campaignId, params);
        const data = res.data?.data ?? res.data;
        participants.value = data?.items || [];
        pagination.totalItems = data?.pagination?.totalItems || 0;
        pagination.totalPages = data?.pagination?.totalPages || 1;
      } catch (e) {
        error.value = e.response?.data?.message || e.message || '載入參與者失敗';
        participants.value = [];
      } finally {
        loading.value = false;
      }
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      load();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= pagination.totalPages) {
        currentPage.value = page;
        load();
      }
    };

    const statusLabel = (status) => ({
      active: '進行中',
      completed: '已完成',
      expired: '已過期',
      cancelled: '已取消'
    }[status] || status || '-');

    const statusBadgeClass = (status) => ({
      active: 'badge badge-primary',
      completed: 'badge badge-success',
      expired: 'badge badge-secondary',
      cancelled: 'badge badge-warning'
    }[status] || 'badge badge-secondary');

    const formatDate = (value) => {
      if (!value) return '-';
      return new Date(value).toLocaleString('zh-TW');
    };

    const truncate = (value) => {
      if (!value) return '-';
      if (value.length <= 16) return value;
      return `${value.slice(0, 8)}...${value.slice(-6)}`;
    };

    watch(
      () => [props.campaignId, props.campaignStatus],
      () => {
        currentPage.value = 1;
        load();
      }
    );

    onMounted(() => load());

    return {
      loading,
      error,
      participants,
      filterStatus,
      currentPage,
      pagination,
      load,
      handleFilterChange,
      goToPage,
      statusLabel,
      statusBadgeClass,
      formatDate,
      truncate
    };
  }
};
</script>

<style scoped>
.participants-panel {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
}

.participants-empty {
  text-align: center;
  padding: 48px 16px;
  color: #6c757d;
}

.participants-empty i {
  font-size: 40px;
  margin-bottom: 12px;
  display: block;
}

.participants-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
}

.filter-group label {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}

.filter-group .form-control {
  min-width: 140px;
}

.table-wrapper {
  overflow-x: auto;
}

.table-wrapper table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
}

.table-wrapper th,
.table-wrapper td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.table-wrapper th {
  background: #f8f9fa;
  font-weight: 600;
}

.mono {
  font-family: monospace;
  font-size: 12px;
}

.empty-state {
  text-align: center;
  color: #6c757d;
  padding: 24px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}

.page-info {
  font-size: 13px;
  color: #6c757d;
}

.text-muted {
  color: #adb5bd;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 32px 16px;
}
</style>
